import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl } from '../lib/config.js';
import { getSession } from '../lib/supabase.js';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      try {
        // Supabase client (detectSessionInUrl=true) consumes the hash automatically.
        const { data, error: sessionError } = await getSession();
        if (cancelled) return;

        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        const params = new URLSearchParams(location.search);
        const next = params.get('next');
        const safeNext = next && next.startsWith('/') ? next : '/dashboard/jobs';

        if (!data?.session) {
          // No session yet — bounce back to login.
          navigate(`/login${next ? `?next=${encodeURIComponent(next)}` : ''}`, { replace: true });
          return;
        }

        // Best-effort link: if the auth user matches a legacy WhatsApp /
        // iMessage interviewees row by a Supabase-verified auth phone, server
        // binds user_id. Failures are non-fatal — onboarding will run if no
        // row exists yet.
        try {
          await fetch(apiUrl('/api/v1/auth/link'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${data.session.access_token}`,
            },
            body: JSON.stringify({}),
          });
        } catch (linkError) {
          console.warn('auth/link best-effort failed (continuing)', linkError);
        }

        navigate(safeNext, { replace: true });
      } catch (err) {
        if (cancelled) return;
        console.error('AuthCallback error', err);
        setError(err?.message || 'Sign-in failed. Please try again.');
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [navigate, location.search]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,#CCFBF1_0,#F8FAFC_38%,#F1F5F9_100%)] px-4 text-slate-700">
      <div className="w-full max-w-sm rounded-[16px] border border-white/70 bg-white/95 p-7 text-center shadow-2xl shadow-slate-900/10 backdrop-blur">
        {error ? (
          <>
            <p className="font-display text-2xl text-obsidian-400">Sign-in failed</p>
            <p className="mt-3 text-sm text-coral-700">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-5 inline-flex h-11 items-center justify-center rounded-[8px] bg-teal-600 px-5 text-sm font-bold text-white hover:bg-teal-700"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-teal-600" />
            <p className="mt-5 font-display text-2xl text-obsidian-400">Signing you in…</p>
            <p className="mt-2 text-sm text-slate-600">One sec while we finalize your session.</p>
          </>
        )}
      </div>
    </main>
  );
}
